import {
  ConfigFormItem,
  IWidgetComponentProps,
  RichTextAdvancedRender,
  getWidgetProps,
} from '@framework/engine';
import { DraggerInput, UploadInput } from '@framework/form';
import { useWatch } from 'react-hook-form';
import { AiFileCheckMessage } from '../../../../ai/AiFileCheckMessage';
import { useFillableSubmissionContext } from '../../../FillableSubmissionContext';
import { IUploadWidget, UploadWidgetKind } from '../IUploadWidget';
import { publicDataUploadWidgetName } from '../UploadWidget.type';
import { uploadWidgetVerifyDocumentAiField } from '../createUploadWidgetFormSchema';
import { UploadedFileRow } from './UploadedFileRow';
import {
  getSectionIdFromFieldName,
  useUploadWidgetTitle,
} from './useUploadWidget';
import { useUploadWidgetFileList } from './useUploadWidgetFileList';

export interface IUploadWidgetReadonlyCompontentInputProps
  extends IWidgetComponentProps<IUploadWidget> {
  widgetName: string;
  publicUpload?: boolean;
}

export function UploadWidgetReadonlyCompontentInput(
  props: IUploadWidgetReadonlyCompontentInputProps,
) {
  const { widget, widgetName, publicUpload } = props;
  const options = getWidgetProps(props);

  const fieldName = publicUpload
    ? publicDataUploadWidgetName(options.name)
    : options.name;

  const verifyDocumentAiFieldName =
    uploadWidgetVerifyDocumentAiField(fieldName);

  const verifyDocumentAiValue = useWatch({
    name: verifyDocumentAiFieldName,
  });

  const title = useUploadWidgetTitle(widget);

  const { fileList } = useUploadWidgetFileList(fieldName);

  const isButton = widget.kind === UploadWidgetKind.Button;

  const { submissionId, submissionModuleId } = useFillableSubmissionContext();
  const sectionId = getSectionIdFromFieldName(widgetName);

  return (
    <>
      <ConfigFormItem
        name={widgetName}
        label={<RichTextAdvancedRender text={options.label} />}
        help={isButton && <RichTextAdvancedRender text={options.helpText} />}
        layout={{ hideVisualLabel: options.hideLabel }}
        readonly={options.readonly}
      >
        {isButton ? (
          <UploadInput
            name={widgetName}
            title={title}
            showUploadList={false}
            disabled={true}
            buttonProps={{ disabled: true }}
          />
        ) : (
          <DraggerInput
            name={widgetName}
            title={title}
            showUploadList={false}
            disabled={true}
            info={
              options.helpText ? (
                <RichTextAdvancedRender text={options.helpText} />
              ) : (
                <div />
              )
            }
          />
        )}
        <div>
          {fileList.map(file => (
            <UploadedFileRow
              key={file.uid}
              file={file}
              submissionId={submissionId}
              submissionModuleId={submissionModuleId}
              sectionId={sectionId}
              code={widget.code}
              readonly
              reset={() => {}}
              omissis={publicUpload}
              extra={
                <AiFileCheckMessage
                  result={verifyDocumentAiValue}
                  isBackoffice={undefined}
                  isReadonly={options.readonly}
                />
              }
              isImage={widget.isImage}
            />
          ))}
        </div>
      </ConfigFormItem>
    </>
  );
}
